/**
 * Router-aware link: internal paths use React Router; external use <a>.
 */
import { forwardRef, type AnchorHTMLAttributes, type ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

export interface AppLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  href?: string | null;
  children?: ReactNode;
  /** Replace the history entry instead of pushing a new one. */
  replace?: boolean;
  /* Accepted for `next/link` call-site compatibility; deliberately unused. */
  prefetch?: boolean;
  scroll?: boolean;
  shallow?: boolean;
}

function isExternal(href: string | null | undefined): boolean {
  if (href == null || typeof href !== "string") return false;
  return (
    href.startsWith("http://") ||
    href.startsWith("https://") ||
    href.startsWith("//") ||
    href.startsWith("mailto:") ||
    href.startsWith("tel:") ||
    href.startsWith("#")
  );
}

const Link = forwardRef<HTMLAnchorElement, AppLinkProps>(function Link(
  {
    href,
    prefetch: _prefetch,
    replace,
    scroll: _scroll,
    shallow: _shallow,
    children,
    ...props
  },
  ref,
) {
  const to = href ?? "/";

  if (isExternal(to)) {
    return (
      <a ref={ref} href={to} {...props}>
        {children}
      </a>
    );
  }

  return (
    <RouterLink ref={ref} to={to} replace={replace} {...props}>
      {children}
    </RouterLink>
  );
});

export default Link;
